import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import API from '@/api';
import { queryKeys } from '@/api/utils/queryKeys';
import { ErrorIds } from '@/constants/ApiResponse';
import { meQueryOptions } from '@/queries/meQuery/meQuery';
import { parseApiError, reportException } from '@/utils/error';
import { toE164SeMobile } from '@/utils/SE/mobile';
import { assertCondition } from '@/utils/typescript';

type Options = {
  onSuccess: (data: { resendAfter: string; mobile: string }) => void;
  onAlreadyVerified: () => void;
  onRequest?: () => void;
};

const useRequestMobileVerificationCode = ({ onSuccess, onAlreadyVerified, onRequest }: Options) => {
  const { t } = useTranslation(['Modal', 'FormErrors']);
  const { data: me } = useQuery(meQueryOptions());
  const queryClient = useQueryClient();
  const requestedMobileRef = useRef('');

  const handleSuccess = ({ resendAfter }: { resendAfter: string }) => {
    onSuccess({ resendAfter, mobile: requestedMobileRef.current });
  };

  const handleAlreadyVerified = useCallback(
    async (error: unknown) => {
      reportException(
        new Error('Mobile already verified when requesting verification code', { cause: error }),
        {
          level: 'warning',
        },
      );
      // Await creditActions refresh before onAlreadyVerified: ResumeWithdraw reads the cache via
      // getQueryData and would otherwise reopen the same verification modal on stale reasons.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.me }),
        queryClient.invalidateQueries({ queryKey: queryKeys.creditActions, refetchType: 'all' }),
      ]);
      onAlreadyVerified();
    },
    [onAlreadyVerified, queryClient],
  );

  const {
    mutate: sendCode,
    isPending: isSendPending,
    isSuccess: isSendSuccess,
    error: sendCodeError,
    reset: resetSendCode,
  } = useMutation({
    mutationFn: API.user.sendMobileVerificationCode,
    onSuccess: handleSuccess,
    onError: async error => {
      if (parseApiError(error).errorId === ErrorIds.CONFLICT) {
        await handleAlreadyVerified(error);
      }
    },
  });

  const {
    mutate: updateMobile,
    isPending: isUpdatePending,
    isSuccess: isUpdateSuccess,
    error: updateMobileError,
    reset: resetUpdateMobile,
  } = useMutation({
    mutationFn: API.user.updateMobile,
    onSuccess: handleSuccess,
  });

  const reset = useCallback(() => {
    resetSendCode();
    resetUpdateMobile();
  }, [resetSendCode, resetUpdateMobile]);

  const requestMobileVerificationCode = useCallback(
    (mobile: string) => {
      assertCondition(!!me, 'Me data must exist before requesting mobile verification code');
      reset();
      const e164Mobile = toE164SeMobile(mobile);
      requestedMobileRef.current = e164Mobile;
      onRequest?.();

      if (e164Mobile === toE164SeMobile(me.mobile)) {
        sendCode();
      } else {
        updateMobile(e164Mobile);
      }
    },
    [me, onRequest, reset, sendCode, updateMobile],
  );

  // sendCode CONFLICT is handled by onAlreadyVerified — don't surface it as a form error
  const errorMessage = useMemo(() => {
    if (updateMobileError) {
      return parseApiError(updateMobileError).errorId === ErrorIds.CONFLICT
        ? t('Modal:MobileVerification.errorDuplicateMobile')
        : t('FormErrors:somethingWentWrong');
    }

    if (sendCodeError && parseApiError(sendCodeError).errorId !== ErrorIds.CONFLICT) {
      return t('FormErrors:somethingWentWrong');
    }

    return undefined;
  }, [sendCodeError, updateMobileError, t]);

  return {
    requestMobileVerificationCode,
    isPending: isSendPending || isUpdatePending,
    isSuccess: isSendSuccess || isUpdateSuccess,
    errorMessage,
    reset,
  };
};

export default useRequestMobileVerificationCode;
